import {
	Box,
	Flex,
	Grid,
	GridItem,
	Link,
	Spinner,
	Text,
} from '@chakra-ui/react';
import type React from 'react';
import { useNavigate } from 'react-router';
import FaceliftBadge from '@/components/Atoms/FaceliftBadge';
import { FaceliftScrollContainer } from '@/components/Atoms/FaceliftScrollContainer';
import FaceliftText from '@/components/Atoms/FaceliftText';
import Show from '@/components/Molecules/Show';
import { useCallback } from 'react';

interface DetailField {
	label: string;
	value: string | number | undefined | null | string[];
	link?: string; // Optional: External URL to make value clickable (opens in new tab)
	linkText?: string; // Optional: Custom text for the link (if different from value)
	navigateTo?: string; // Optional: Internal route to navigate to (uses React Router)
}

interface DetailColumn {
	data: DetailField[];
}

interface DetailsViewProps {
	data: DetailColumn[];
	isLoading?: boolean;
	gridConfig?: {
		base?: string;
		md?: string;
		lg?: string;
		xl?: string;
	};
	gap?: {
		base?: number;
		md?: number;
		lg?: number;
		xl?: number;
	};
	dialogWidth?: string;
}

const ScalarDetailField = ({
	label,
	value,
	link,
	linkText,
	navigateTo,
}: DetailField) => {
	const navigate = useNavigate();

	const handleInternalNavigation = useCallback(() => {
		if (navigateTo) {
			console.log("navigateTo", navigateTo);
			window.location.href = navigateTo;
		}
	}, [navigate, navigateTo]);
	
	// Use larger gap for Products field
	const gapSize = label === 'Products' ? 8 : 2;
	
	return (
		<Box display="flex" justifyContent="space-between" gap={gapSize}>
			<FaceliftText fontSize="sm" fontWeight="bold">
				{label}
			</FaceliftText>
			<FaceliftText fontSize="sm" fontWeight={400} textAlign="right">
				<Show when={!!link}>
					<Link
						href={link}
						target="_blank"
						rel="noopener noreferrer"
						color="brand.600"
						textDecoration="none"
						_hover={{ color: 'brand.500' }}
					>
						{linkText || value?.toString() || '-'}
					</Link>
				</Show>
				<Show when={!link && !!navigateTo}>
					<Link
						as="button"
						onClick={handleInternalNavigation}
						color="brand.600"
						textDecoration="none"
						cursor="pointer"
						_hover={{ color: 'brand.500', textDecoration: 'underline' }}
						textAlign="right"
					>
						{linkText || value?.toString() || '-'}
					</Link>
				</Show>
				<Show when={!link && !navigateTo}>{value?.toString() || '-'}</Show>
			</FaceliftText>
		</Box>
	);
};

const ArrayDetailField = ({ label, value }: DetailField) => (
	<Box display="flex" flexDirection="column" gap={2} width="100%">
		<Text fontSize="sm" fontWeight="bold">
			{label}
		</Text>
		<Flex flexWrap="wrap" gap={2}>
			{Array.isArray(value) && value.length > 0 ? (
				value.map((item) => (
					// <Badge key={`${label}-${item}`}>{item}</Badge>
					<FaceliftBadge
						key={`${label}-${item}`}
						status={item}
						badgeColor="brand.solid"
					/>
				))
			) : (
				<Text fontSize="sm" fontWeight={400}>
					-
				</Text>
			)}
		</Flex>
	</Box>
);

const DetailsView: React.FC<DetailsViewProps> = ({
	data,
	isLoading = false,
	gridConfig = {
		base: '1fr',
		md: 'repeat(3, 1fr)',
	},
	gap = {
		base: 3,
		md: 10,
	},
}) => {
	const scalarColumns: DetailColumn[] = [];
	const arrayFields: DetailField[] = [];
	const safeData = data ?? [];

	safeData.forEach((column) => {
		const scalarFields: DetailField[] = [];

		column.data.forEach((field) => {
			if (Array.isArray(field.value)) {
				arrayFields.push(field);
			} else {
				scalarFields.push(field);
			}
		});

		if (scalarFields.length > 0) {
			scalarColumns.push({ data: scalarFields });
		}
	});

	return (
		<FaceliftScrollContainer overflowY="auto" minWidth="fit-content">
			<Show when={isLoading}>
				<Flex justify="center" align="center" minHeight="300px" width="100%">
					<Spinner size="xl" colorPalette="brand" />
				</Flex>
			</Show>
			<Show when={data && data.length > 0 && !isLoading}>
				<Box display="flex" flexDirection="column" gap={6}>
					<Show when={scalarColumns.length > 0}>
						<Grid
							templateColumns={{
								base: gridConfig.base || '1fr',
								md: gridConfig.md || 'repeat(3, 1fr)',
								lg: gridConfig.lg,
								xl: gridConfig.xl,
							}}
							gap={{
								base: gap.base || 3,
								md: gap.md || 10,
								lg: gap.lg,
								xl: gap.xl,
							}}
						>
							{scalarColumns.map((column) => (
								<GridItem
									key={`column-${column.data[0]?.label || Math.random()}`}
								>
									<Grid templateColumns="1fr" gap={{ base: 3, md: 6 }}>
										{column.data.map((field) => (
											<GridItem key={field.label}>
												<ScalarDetailField
													label={field.label}
													value={field.value}
													link={field.link}
													linkText={field.linkText}
													navigateTo={field.navigateTo}
												/>
											</GridItem>
										))}
									</Grid>
								</GridItem>
							))}
						</Grid>
					</Show>

					<Show when={arrayFields.length > 0}>
						<Grid templateColumns="1fr" gap={4}>
							{arrayFields.map((field) => (
								<GridItem key={`array-${field.label}`}>
									<ArrayDetailField label={field.label} value={field.value} />
								</GridItem>
							))}
						</Grid>
					</Show>
				</Box>
			</Show>

			<Show when={(!data || data.length === 0) && !isLoading}>
				<Box textAlign="center" py={8}>
					<Text fontSize="md" color="gray.500">
						No data found
					</Text>
				</Box>
			</Show>
		</FaceliftScrollContainer>
	);
};

export default DetailsView;
